import { useEffect, useState } from 'react'

import type { FlagsVariant } from 'mixpanel-browser'

import { getFeatureFlagVariant, isMeasurementEnabled } from '@/api/tracking'
import { isLocalEnvironment, isTestEnvironment } from '@/environment'
import { useCookieConsent } from '@/hooks/useCookieConsent'

import type { MixpanelFeatureFlagKey, MixpanelFeatureFlagVariant } from './constants'
import { MIXPANEL_FEATURE_FLAG_CONTROL_VARIANT, MIXPANEL_FEATURE_FLAG_DEFINITIONS } from './constants'

export interface UseMixpanelFeatureFlagResult<K extends MixpanelFeatureFlagKey> {
    variant: MixpanelFeatureFlagVariant<K>
    isReady: boolean
}

function isAllowedVariant<K extends MixpanelFeatureFlagKey>(
    flagKey: K,
    key: string,
): key is MixpanelFeatureFlagVariant<K> {
    return (MIXPANEL_FEATURE_FLAG_DEFINITIONS[flagKey].variants as readonly string[]).includes(key)
}

function resolveFlagId(flagKey: MixpanelFeatureFlagKey): string {
    const { flagIds } = MIXPANEL_FEATURE_FLAG_DEFINITIONS[flagKey]
    return isTestEnvironment ? flagIds.test : flagIds.production
}

export function useMixpanelFeatureFlag<K extends MixpanelFeatureFlagKey>(flagKey: K): UseMixpanelFeatureFlagResult<K> {
    const { preferences } = useCookieConsent()
    const flagId = resolveFlagId(flagKey)
    const fallbackVariant = MIXPANEL_FEATURE_FLAG_CONTROL_VARIANT as MixpanelFeatureFlagVariant<K>
    const fallbackPayload: FlagsVariant = { key: fallbackVariant, value: undefined }

    const [variant, setVariant] = useState<MixpanelFeatureFlagVariant<K>>(fallbackVariant)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        let cancelled = false

        if (!preferences.measurement || !isMeasurementEnabled()) {
            setVariant(fallbackVariant)
            setReady(true)
            return
        }

        getFeatureFlagVariant(flagId, fallbackPayload)
            .then(resolved => {
                if (!cancelled) {
                    const key = resolved.key
                    setVariant(isAllowedVariant(flagKey, key) ? key : fallbackVariant)
                    setReady(true)
                }
            })
            .catch(error => {
                if (isLocalEnvironment) {
                    console.error('[Mixpanel feature flag]', flagId, error)
                }
                if (!cancelled) {
                    setVariant(fallbackVariant)
                    setReady(true)
                }
            })

        return () => {
            cancelled = true
        }
    }, [flagKey, flagId, preferences.measurement])

    return { variant, isReady: ready }
}
